import React, { FC, PropsWithChildren } from 'react'

import { css, cx } from '@linaria/core'
import { styled } from '@linaria/react'

import { breakpoints } from 'components/designSystem/shared/breakpoints'
import { ContainerSize } from 'components/designSystem/shared/containerSize'
import { SidebarSize } from 'components/designSystem/shared/sidebar'
import { zIndex } from 'components/designSystem/zIndex'
import { useHasUserRestrictions } from 'functions/hasUserRestrictions'
import { useLoveMailruPartner } from 'hooks/useLoveMailruPartner'

import { useRemoveVisualEffectForPath } from './hooks/useRemoveVisualEffectForPath'
import { LoveMailru } from './LoveMailru'
import { NavBar } from './NavBar'
import { TabBar } from './TabBar'
import { StickyObserver } from '../StickyObserver/StickyObserver'

/**
 * При прокрутке появляется border у navigation
 * Но так как некоторые мобильные браузеры обладают bounce scroll overflow
 * То данная конструкция работает обе стороны. Поэтому там где нет скролла(профиль и рейтинг)
 * Отключаем через useRemoveVisualEffectForPath
 */
export const Header: FC = () => {
  const hasRemove = useRemoveVisualEffectForPath()
  const isLoveMailruPartner = useLoveMailruPartner()
  const userHasRestrictions = useHasUserRestrictions()

  return (
    <Parent
      data-name="main-navigation"
      position="top"
      className={cx(
        hasRemove && emptyBackgroundCss,
        hasRemove && noStickyCss,
        isLoveMailruPartner && loveMailruCss,
        userHasRestrictions && restrictionCss
      )}
      isDisabledEventScroll={hasRemove}
    >
      <BodyPartnerSwitch>
        <TabBarDesktop data-name="tab-bar-desktop" />
        <NavBar />
      </BodyPartnerSwitch>
    </Parent>
  )
}

const BodyPartnerSwitch: FC<PropsWithChildren> = ({ children }) => {
  const isLoveMailruPartner = useLoveMailruPartner()
  const userHasRestrictions = useHasUserRestrictions()

  if (isLoveMailruPartner) {
    return (
      <BodyMailru
        className={cx(
          bodyCommonCss,
          userHasRestrictions && bodyCommonRestrictionCss
        )}
      >
        <LoveMailru />
        {children}
      </BodyMailru>
    )
  }

  return (
    <div
      className={cx(
        bodyCommonCss,
        userHasRestrictions && bodyCommonRestrictionCss
      )}
    >
      {children}
    </div>
  )
}

const Parent = styled(StickyObserver)`
  display: grid;
  grid-auto-flow: column;
  position: sticky;
  top: 0;
  justify-content: center;
  align-items: center;
  transition: box-shadow 0.2s ease-in-out;
  z-index: ${zIndex.default};
  background: var(--gradient-top-vertical);
  min-width: 100%;

  @media (max-width: ${breakpoints.mobile}px) {
    justify-content: stretch;
    margin-bottom: 0;
  }

  @media (min-width: ${breakpoints.mobile + 1}px) {
    grid-template-rows: var(--spacing-24px) auto 0;
  }

  @media (min-width: ${breakpoints.startDesktop}px) {
    grid-template-rows: var(--spacing-24px) auto var(--spacing-16px);
  }

  @media (min-width: ${breakpoints.largeDesktop}px) {
    grid-template-rows: var(--spacing-64px) auto var(--spacing-16px);
  }
`

const loveMailruCss = css`
  @media (min-width: ${breakpoints.mobile + 1}px) {
    grid-template-rows: var(--spacing-64px) auto 0;
  }

  z-index: ${zIndex.mailruMenuBurger};
`

const restrictionCss = css`
  grid-template-rows: var(--spacing-24px) auto 0;
`

const emptyBackgroundCss = css`
  background: none;
`

const bodyCommonCss = css`
  display: grid;
  gap: var(--spacing-32px);
  grid-gap: var(--spacing-32px);
  grid-row: 2;
  height: var(--spacing-48px);

  @media (min-width: ${breakpoints.mobile + 1}px) {
    width: ${ContainerSize.tablet}px;
  }

  @media (min-width: ${breakpoints.startDesktop}px) {
    width: ${ContainerSize.start}px;
    grid-template-columns: ${SidebarSize.middle}px 1fr;
  }

  @media (min-width: ${breakpoints.middleDesktop}px) {
    width: ${ContainerSize.middle}px;
  }

  @media (min-width: ${breakpoints.largeDesktop}px) {
    width: ${ContainerSize.large}px;
    grid-template-columns: ${SidebarSize.large}px 1fr;
  }
`

const bodyCommonRestrictionCss = css`
  grid-template-columns: auto;
`

const BodyMailru = styled.div`
  grid-auto-flow: row;

  @media (max-width: ${breakpoints.mobile}px) {
    height: calc(var(--spacing-48px) * 2);
    gap: unset;
    grid-gap: unset;
    grid-template-rows: 1fr 1fr;
    align-items: center;
  }
`

const noStickyCss = css`
  position: static;
`

const TabBarDesktop = styled(TabBar)`
  display: flex;
  justify-content: space-around;
  padding: 0 var(--spacing-24px);

  @media (max-width: ${breakpoints.startDesktop - 1}px) {
    display: none;
  }
`
